import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const FinalCta = () => {
  const [email, setEmail] = useState("");
  const [brief, setBrief] = useState("");
  const [sending, setSending] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("We need a real email to write back.");
      return;
    }
    setSending(true);
    // The site is static — simulate acknowledgement.
    await new Promise((r) => setTimeout(r, 650));
    setSending(false);
    setEmail("");
    setBrief("");
    toast.success("Brief received. We reply inside 24 hours.");
  };

  return (
    <section
      id="final-cta"
      data-testid="final-cta-container"
      className="relative overflow-hidden pt-32 pb-40 sm:pt-44 sm:pb-56"
    >
      {/* Bloom */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6 }}
        className="absolute inset-0 pointer-events-none"
      >
        <div
          className="bloom"
          style={{
            width: "80vw",
            height: "70vw",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -30%)",
            opacity: 0.4,
          }}
        />
        <div
          className="bloom"
          style={{
            width: "40vw",
            height: "40vw",
            bottom: "-10%",
            left: "10%",
            opacity: 0.25,
            background:
              "radial-gradient(circle, rgba(255,195,100,0.7), transparent 70%)",
          }}
        />
      </motion.div>

      <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />

      <div className="relative max-w-[1280px] mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-20%" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mono-label mb-8 flex items-center gap-3"
        >
          <span className="h-px w-8 bg-accent" />
          — Begin
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-20%" }}
          transition={{ duration: 1.1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-extrabold tracking-tighter uppercase text-[clamp(3rem,10vw,10rem)] leading-[0.88] max-w-[14ch]"
        >
          Let's build
          <br />
          <span className="font-light font-editorial italic text-accent text-glow">
            the quiet one
          </span>{" "}
          <span className="font-light text-[#8A8D98]">in the room.</span>
        </motion.h2>

        <div className="mt-16 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.3 }}
            className="space-y-6 max-w-[48ch]"
          >
            <p className="text-[17px] leading-relaxed text-[#C9CBD3]">
              Two slots open for Q1. Tell us about the brand, the stakes, and
              the one feeling you want visitors to leave with. We'll write back
              inside 24 hours with next steps.
            </p>
            <div className="flex flex-col gap-3 pt-4 border-t border-white/8">
              {[
                ["Email", "studio@zcrazy.com"],
                ["Studio", "Berlin · Remote"],
                ["Response", "Inside 24 hours"],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5A5D68]">
                    {k}
                  </span>
                  <span className="text-[14px] text-[#F4F4F2]">{v}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="glass-strong rounded-3xl p-6 sm:p-8 space-y-5"
          >
            <div>
              <label className="mono-label block mb-2" htmlFor="cta-email">
                Email
              </label>
              <input
                id="cta-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@founder.com"
                data-testid="final-cta-email-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-accent focus:outline-none py-3 text-[17px] text-[#F4F4F2] placeholder:text-[#4A4D55] transition-colors"
              />
            </div>
            <div>
              <label className="mono-label block mb-2" htmlFor="cta-brief">
                In one sentence, the project
              </label>
              <textarea
                id="cta-brief"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                rows={3}
                placeholder="A static launch page for our new photography book, early March."
                data-testid="final-cta-brief-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-accent focus:outline-none py-3 text-[15px] text-[#F4F4F2] placeholder:text-[#4A4D55] transition-colors resize-none"
              />
            </div>
            <div className="pt-2 flex items-center justify-between gap-4">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5A5D68]">
                No forms farm. Just us two.
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="final-cta-submit-button"
                className="btn-primary disabled:opacity-60"
              >
                {sending ? "Sending…" : "Send brief"}
                <ArrowUpRight size={16} />
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default FinalCta;
